import React, { Component } from "react";
import _ from "lodash";
import { cancellablePromise, delay } from "../../_utils/generalUtils";
import "./styles/table.css";

class TableBody extends Component {
  state = {
    isTouchMove: false,
  };

  componentWillUnmount() {
    this.cancelAllClickPromises();
  }

  pendingClickPromises = [];

  renderCell = (item, column) => {
    // If content renderer is provided, use that instead of
    // the provided path.
    if (column.content) return column.content(item);
    return _.get(item, column.path);
  };

  /**
   * Returns a unique td key.
   * For tables with composite key, an example of TdKey is:
   * "tableTdKey_laborFullName_128_2004", where 'laborFullName'
   * is the column path string, '128' is laborId, and '2004' is
   * workPerformedId.
   */
  createTdKey = (item, column) => {
    const { idPropName, idPropName2 } = this.props;
    // If idPropName2 is not provided, return key for just idPropName
    if (!idPropName2)
      return `tableTdKey_${column.path || column.key}_${item[idPropName]}`;
    return `tableTdKey_${column.path || column.key}_${item[this.props.idPropName]}_${
      item[this.props.idPropName2]
    }`;
  };

  createTotalsTdKey = (path) => `tableTdTotal_${path}`;

  addClickPromise = (cp) => this.pendingClickPromises.push(cp);
  removeClickPromise = (cp) =>
    (this.pendingClickPromises = this.pendingClickPromises.filter((p) => p !== cp));
  cancelAllClickPromises = () => this.pendingClickPromises.map((cp) => cp.cancel());

  /**
   * Click event handler for <tr>.
   * Filters out dblClicks and scrolling events so that only single clicks are
   * enforced as valid action. Calls the client-provided handleClick_tr.
   */
  _handleClick_tr = (item) => (e) => {
    e.preventDefault();

    // targetId is the <tr>'s element id, passed into the client-provided
    // handleClick_tr()'s optional second param - which allows further
    // conditional processing based on <tr>'s "type" (i.e. "isTotalsRow").
    const targetId = e.target.id;

    // Prevent double-calling of _handleClick_tr from dblClicks. "First" click
    // initiates a delay of 200 milliseconds, and only proceeds with the
    // requested action if "second" click is not registered before this delay
    // is over. Delay can be adjusted to optimize UX.
    const waitForDblClick = cancellablePromise(delay(200));
    this.addClickPromise(waitForDblClick);

    // Do not call _handleClick_tr when user is scrolling on a touch device.
    if (!this.state.isTouchMove) {
      this.setState({ isTouchMove: false });
      return waitForDblClick.promise
        .then(() => {
          this.removeClickPromise(waitForDblClick);

          // See above comments for targetId for its purpose.
          // targetId may be disregarded by client-provided handleClick_tr.
          this.props.handleClick_tr(item, targetId);
        })
        .catch((errorInfo) => {
          this.removeClickPromise(waitForDblClick);
          if (!errorInfo.isCanceled) throw errorInfo.error;
        });
    }
    this.setState({ isTouchMove: false });
  };

  _handleDoubleClick_tr = (item) => (e) => {
    e.preventDefault();

    this.cancelAllClickPromises();
    this.props.handleDoubleClick_tr && this.props.handleDoubleClick_tr(item);
  };

  _handleClick_showMore = (e) => {
    e.preventDefault();
    const { getNextPage } = this.props;
    getNextPage && getNextPage();
  };

  _handleTouchMove = () => this.setState({ isTouchMove: true });

  _handleTouchEnd_tr = (item) => (e) => {
    if (this.pendingClickPromises.length === 0) {
      const targetId = e.target.id;
      e.preventDefault();

      const waitForDblClick = cancellablePromise(delay(200));
      this.addClickPromise(waitForDblClick);

      if (!this.state.isTouchMove) {
        this.setState({ isTouchMove: false });
        return waitForDblClick.promise
          .then(() => {
            this.removeClickPromise(waitForDblClick);
            this.props.handleClick_tr(item, targetId);
          })
          .catch((errorInfo) => {
            this.removeClickPromise(waitForDblClick);
            if (!errorInfo.isCanceled) throw errorInfo.error;
          });
      }
      this.setState({ isTouchMove: false });
    }
  };

  calcTotal = (column) => {
    const { data } = this.props;
    const reducer = (accumulator, currValue) => accumulator + currValue;
    const valuesToAdd = data.map((i) => {
      if (column.calculated) return column.equation(i);
      if (!isNaN(i[column.path])) return parseFloat(i[column.path]);
      return 0;
    });
    if (valuesToAdd.length > 0) {
      if (column.totalsFormatter)
        return column.totalsFormatter(valuesToAdd.reduce(reducer));
      return valuesToAdd.reduce(reducer);
    }
    return 0;
  };

  getTrKey = (item) => {
    const { idPropName, idPropName2 } = this.props;
    if (!idPropName2) return `tableTrKey_${item[idPropName]}`;
    return `tableTrKey_${item[idPropName]}_${item[idPropName2]}`;
  };

  getTrClassName = (item) => {
    const { selectedItemId, selectedItemId2, idPropName, idPropName2 } = this.props;
    let className = "pointer-cursor";
    if (!idPropName2)
      className =
        selectedItemId === item[idPropName]
          ? "pointer-cursor table-primary"
          : "pointer-cursor";
    // If composite ID is provided:
    else if (idPropName2 && selectedItemId2)
      className =
        selectedItemId === item[idPropName] && selectedItemId2 === item[idPropName2]
          ? "pointer-curosr table-primary"
          : "pointer-cursor";
    // Default fallback action
    return className;
  };

  render() {
    const { data, columns, isPaginated, currPage, pageCount, fetching } = this.props;
    const columnsWithTotals = columns.filter((c) => c.showTotals === true);
    const isLastPage = isPaginated && currPage === pageCount;

    return (
      <tbody>
        {data.map((item) => (
          <tr
            className={this.getTrClassName(item)}
            key={this.getTrKey(item)}
            onClick={this._handleClick_tr(item)}
            onTouchMove={this._handleTouchMove}
            onTouchEnd={this._handleTouchEnd_tr(item)}
            onDoubleClick={this._handleDoubleClick_tr(item)}
          >
            {columns.map((column) => (
              <td key={this.createTdKey(item, column)} className={column.tdClassName}>
                {this.renderCell(item, column)}
              </td>
            ))}
          </tr>
        ))}
        {isPaginated && !isLastPage && (
          <tr className="pointer-cursor" id="showMore" key={`tableTrKey_showMore`}>
            <td
              colSpan={columns.length + 1}
              className="w-100"
              style={{ textAlign: "center" }}
            >
              <button
                name="showMore"
                id="showMore"
                className="btn btn-sm btn-link py-0"
                onClick={this._handleClick_showMore}
                disabled={fetching}
              >
                Show More...
              </button>
            </td>
          </tr>
        )}
        {columnsWithTotals.length > 0 && (
          <tr className="pointer-cursor" id="isTotalsRow" key={`tableTrKey_totals`}>
            {columns.map((c) => {
              if (c.showTotals === true)
                return (
                  <td key={this.createTotalsTdKey(c.path)} className={c.tdClassName}>
                    {this.calcTotal(c)}
                  </td>
                );
              if (c.showTotalsLabel)
                return (
                  <td key={this.createTotalsTdKey(c.path)}>
                    <b>Total =</b>
                  </td>
                );
              if (c.totalsUnit !== null)
                return <td key={this.createTotalsTdKey(c.path)}>{c.totalsUnit}</td>;
              return <td />;
            })}
          </tr>
        )}
      </tbody>
    );
  }
}

export default TableBody;
